<template>
    <!-- NOTE: Mount target must stay in the layout (never `display: none`)
         while Stripe initializes, otherwise Express Checkout fails to render
         on first load and only appears after a refresh. We collapse the
         container to 0 height until Stripe fires 'ready', then reveal it. -->
    <div
        id="express-checkout-element"
        class="payment-request-btn-container"
        :class="{ 'express-checkout-collapsed': !canMakePayment }"
    ></div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";

let stripe = null;
let paymentElements = null;

export default {
    name: 'ExpressCheckoutButton',
    props: {
        amount: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            canMakePayment: false,
            expressCheckoutElement: null,
        };
    },
    async mounted() {
        if (this.amount > 0) {
            await this.init();
        }
    },
    watch: {
        amount(newAmount, oldAmount) {
            if (newAmount > 0 && !paymentElements) {
                this.init();
                return;
            }
            if (paymentElements && newAmount > 0 && newAmount !== oldAmount) {
                paymentElements.update({ amount: newAmount });
            }
        },
    },
    methods: {
        async init() {
            try {
                stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
                if (!stripe) return;

                paymentElements = stripe.elements({
                    mode: 'payment',
                    amount: this.amount,
                    currency: 'usd',
                    paymentMethodCreation: 'manual',
                });

                this.mountExpressCheckout({ allowLink: false });
            } catch (error) {
                console.error('Failed to initialize express checkout:', error);
            }
        },

        mountExpressCheckout({ allowLink }) {
            const paymentMethods = {
                paypal: 'never',
                amazonPay: 'never',
            };
            if (!allowLink) paymentMethods.link = 'never';

            this.expressCheckoutElement = paymentElements.create('expressCheckout', {
                buttonHeight: 48,
                paymentMethods,
                paymentMethodOrder: ['googlePay', 'applePay', 'link'],
                layout: {
                    overflow: 'never',
                },
            });
            this.expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
                // NOTE: First try GPay/Apple Pay only. If neither is available,
                // recreate with Link enabled as fallback.
                if (!allowLink && !availablePaymentMethods) {
                    this.expressCheckoutElement.unmount();
                    this.expressCheckoutElement.destroy();
                    this.expressCheckoutElement = null;
                    this.mountExpressCheckout({ allowLink: true });
                    return;
                }
                this.canMakePayment = !!availablePaymentMethods;
                this.$emit('can-make-payment', this.canMakePayment);
            });
            this.expressCheckoutElement.on('click', (event) => {
                event.resolve({
                    emailRequired: true,
                    phoneNumberRequired: false,
                    shippingAddressRequired: true,
                    shippingRates: [
                        { id: 'free', displayName: 'Free shipping', amount: 0 },
                    ],
                });
            });
            this.expressCheckoutElement.on('confirm', this.handleConfirm);
            this.expressCheckoutElement.mount('#express-checkout-element');
        },

        async handleConfirm(event) {
            try {
                const { error: submitError } = await paymentElements.submit();
                if (submitError) {
                    this.$emit('error', submitError.message || 'Payment validation failed.');
                    return;
                }

                const billingDetails = {};
                if (event.billingDetails?.name) billingDetails.name = event.billingDetails.name;
                if (event.billingDetails?.email) billingDetails.email = event.billingDetails.email;
                if (event.billingDetails?.address) {
                    billingDetails.address = event.billingDetails.address;
                }

                const { paymentMethod, error } = await stripe.createPaymentMethod({
                    elements: paymentElements,
                    params: Object.keys(billingDetails).length ? { billing_details: billingDetails } : {},
                });

                if (error) {
                    this.$emit('error', error.message || 'Payment failed.');
                    return;
                }

                // Map new API's expressPaymentType ('google_pay'|'apple_pay'|'link')
                // to old API's methodName ('google_pay'|'apple-pay'|'link') for parent compatibility.
                const typeMap = {
                    google_pay: 'google_pay',
                    apple_pay: 'apple-pay',
                    link: 'link',
                };
                const methodName = typeMap[event.expressPaymentType] || event.expressPaymentType;

                this.$emit('token', {
                    tokenId: paymentMethod.id,
                    card: {
                        last4: paymentMethod.card?.last4 || '',
                        exp_month: paymentMethod.card?.exp_month || '',
                        exp_year: paymentMethod.card?.exp_year || '',
                        brand: paymentMethod.card?.brand || '',
                    },
                    payerName: event.billingDetails?.name || '',
                    payerEmail: event.billingDetails?.email || '',
                    methodName,
                    // ! No-op: the new expressCheckout API handles success/fail UI itself.
                    complete: () => {},
                });
            } catch (err) {
                console.error('Express checkout confirm failed:', err);
                this.$emit('error', err.message || 'Payment failed.');
            }
        },

        remount() {
            if (this.expressCheckoutElement) {
                this.$nextTick(() => {
                    this.expressCheckoutElement.mount('#express-checkout-element');
                });
            }
        },
    },
};
</script>

<style scoped>
.payment-request-btn-container {
    min-height: 48px;
}
.express-checkout-collapsed {
    min-height: 0;
    height: 0;
    overflow: hidden;
    margin: 0;
}
</style>
